import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import { submitFeedback } from '../../services/api';
import './FeedbackModal.css';

const MAX_IMAGES = 5;

export default function FeedbackModal({ isOpen, onClose }) {
  const { user } = useUser();
  const [replyEmail, setReplyEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReplyEmail(user?.email || '');
      setSubject('');
      setMessage('');
      setImages([]);
      setPreviews([]);
      setSending(false);
      setSuccess(false);
      setError('');
    }
  }, [isOpen, user?.email]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  // Handle paste event to capture clipboard images
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const newImages = [];
    const newPreviews = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file && images.length + newImages.length < MAX_IMAGES) {
          newImages.push(file);
          newPreviews.push(URL.createObjectURL(file));
        }
      }
    }

    if (newImages.length > 0) {
      setImages((prev) => [...prev, ...newImages]);
      setPreviews((prev) => [...prev, ...newPreviews]);
    }
  }, [images.length]);

  // Attach paste listener to the form container
  useEffect(() => {
    const formEl = formRef.current;
    if (isOpen && formEl) {
      formEl.addEventListener('paste', handlePaste);
      return () => formEl.removeEventListener('paste', handlePaste);
    }
  }, [isOpen, handlePaste]);

  // Handle file input selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(
      (f) => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024
    );
    const remaining = MAX_IMAGES - images.length;
    const toAdd = validFiles.slice(0, remaining);

    setImages((prev) => [...prev, ...toAdd]);
    setPreviews((prev) => [...prev, ...toAdd.map((f) => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  // Remove an image by index
  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index]);
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit the form
  const handleSubmit = async () => {
    setError('');

    if (!subject.trim()) {
      setError('Please enter a subject.');
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message.');
      return;
    }

    setSending(true);
    const result = await submitFeedback(
      subject.trim(),
      message.trim(),
      images,
      replyEmail.trim()
    );

    if (result.error) {
      setError(result.error);
      setSending(false);
    } else {
      setSuccess(true);
      setSending(false);
      setTimeout(() => {
        onClose();
      }, 2500);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && isOpen && !sending) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, sending, onClose]);

  if (!isOpen) return null;

  // Success state
  if (success) {
    return (
      <div className="feedback-modal-overlay" onClick={onClose}>
        <div className="feedback-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="feedback-modal-success">
            <div className="feedback-modal-success-icon">&#10003;</div>
            <h3 className="feedback-modal-success-title">Feedback Sent!</h3>
            <p className="feedback-modal-success-text">
              Thanks for reaching out. I'll get back to you soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="feedback-modal-overlay"
      onClick={() => { if (!sending) onClose(); }}
    >
      <div
        className="feedback-modal-card"
        ref={formRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="feedback-modal-close"
          onClick={onClose}
          disabled={sending}
          aria-label="Close"
        >
          &#x2715;
        </button>

        <h2 className="feedback-modal-title">Send Feedback</h2>
        <p className="feedback-modal-subtitle">
          Bug report, feature idea, or anything else &mdash; send it straight to me.
        </p>

        {/* Bug reporting guidance */}
        <div className="feedback-modal-guidance">
          If it is a bug you are reporting, please take a screenshot of where you are
          experiencing it if you are able to recreate it, and describe the bug in as
          much detail as possible with how you got there.
        </div>

        {/* From email (editable, pre-populated) */}
        <div className="feedback-modal-field">
          <label className="feedback-modal-label">From</label>
          <input
            className="feedback-modal-input"
            type="email"
            value={replyEmail}
            onChange={(e) => setReplyEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={sending}
          />
        </div>

        {/* Subject */}
        <div className="feedback-modal-field">
          <label className="feedback-modal-label">Subject</label>
          <input
            className="feedback-modal-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Bug with screener loading"
            maxLength={200}
            disabled={sending}
          />
        </div>

        {/* Message */}
        <div className="feedback-modal-field">
          <label className="feedback-modal-label">Message</label>
          <textarea
            className="feedback-modal-textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your issue, idea, or feedback..."
            rows={5}
            maxLength={10000}
            disabled={sending}
          />
        </div>

        {/* Image previews */}
        {previews.length > 0 && (
          <div className="feedback-modal-previews">
            {previews.map((src, idx) => (
              <div key={idx} className="feedback-modal-preview-item">
                <img src={src} alt={`Screenshot ${idx + 1}`} />
                <button
                  className="feedback-modal-preview-remove"
                  onClick={() => removeImage(idx)}
                  disabled={sending}
                  aria-label="Remove image"
                >
                  &#x2715;
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Attach images bar */}
        <div className="feedback-modal-image-actions">
          <button
            className="feedback-modal-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || images.length >= MAX_IMAGES}
            type="button"
          >
            Attach Images
          </button>
          <span className="feedback-modal-paste-hint">
            or paste from clipboard (Ctrl+V)
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {images.length > 0 && (
            <span className="feedback-modal-image-count">
              {images.length}/{MAX_IMAGES} images
            </span>
          )}
        </div>

        {/* Error message */}
        {error && <p className="feedback-modal-error">{error}</p>}

        {/* Action buttons */}
        <div className="feedback-modal-buttons">
          <button
            className="feedback-modal-btn-secondary"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className="feedback-modal-btn-primary"
            onClick={handleSubmit}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}
